import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Button } from '../ui/button';
import { AlertCircle, CreditCard, X, Gift } from 'lucide-react';

export default function TrialExpiredBanner() {
  const navigate = useNavigate();
  const { isTrialExpired, subscriptionStatus } = useSubscription();
  const [dismissed, setDismissed] = React.useState(false);
  const [trialDismissed, setTrialDismissed] = React.useState(false);

  // If the underlying subscription doc is active (e.g. Stripe-paid, admin-extended, or migrated),
  // suppress both banners — UNLESS the free/trial period has actually expired (trial_expired flips
  // true while status can still read 'active'), in which case the red expiry banner must show.
  const hasActiveSubscription =
    subscriptionStatus?.subscription?.status === 'active' && !subscriptionStatus?.trial_expired;

  const isTrial = subscriptionStatus?.is_trial && !isTrialExpired;
  const trialDaysRemaining = React.useMemo(() => {
    if (!subscriptionStatus?.trial_end_date) return 0;
    const end = new Date(subscriptionStatus.trial_end_date);
    return Math.max(0, Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24)));
  }, [subscriptionStatus?.trial_end_date]);

  // Expired banner — but never show if user has an active paid subscription
  if (isTrialExpired && !dismissed && !hasActiveSubscription) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white py-3 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <span className="font-semibold">Your free trial has expired!</span>
              <span className="hidden sm:inline ml-2">
                Subscribe now to continue using {subscriptionStatus?.tier?.toUpperCase() || 'premium'} features.
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate('/landlord/subscription')}
              className="bg-white text-red-600 hover:bg-gray-100"
              data-testid="trial-expired-subscribe-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Subscribe Now
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setDismissed(true)}
              className="text-white hover:bg-red-700 h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active trial upgrade prompt — also suppressed once user has an active paid sub
  if (isTrial && trialDaysRemaining > 0 && !trialDismissed && !hasActiveSubscription) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#2F4F4F] to-[#3a5f5f] text-white py-2.5 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Gift className="w-5 h-5 flex-shrink-0 text-[#C85A42]" />
            <div className="text-sm">
              <span className="font-medium">{trialDaysRemaining} days left</span>
              <span className="hidden sm:inline ml-1 text-white/80">
                on your free trial. Ready to upgrade?
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => navigate('/landlord/subscription')}
              className="bg-[#C85A42] hover:bg-[#B04A35] text-white h-8 text-xs"
              data-testid="trial-upgrade-btn"
            >
              <CreditCard className="w-3.5 h-3.5 mr-1.5" />
              Upgrade Now
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setTrialDismissed(true)}
              className="text-white/70 hover:text-white hover:bg-white/10 h-7 w-7"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
